import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Briefcase,
  Edit,
  Trash2,
  Users,
  DollarSign,
  Clock,
  Building2,
  AlertTriangle,
  StickyNote,
} from 'lucide-react';
import { fetchJob, fetchJobApplications, deleteJob } from '@/lib/api';
import { JOB_TYPE_LABELS, STATUS_LABELS, STAGE_LABELS, JobType, JobStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getInitials, capitalizeFirst } from '@/lib/utils';
import { NotesSection } from '@/components/notes';

function getStatusBadgeVariant(status: JobStatus): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'open':
      return 'success';
    case 'filled':
      return 'default';
    case 'paused':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    case 'draft':
    default:
      return 'secondary';
  }
}

function getPriorityBadgeVariant(priority: string): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (priority) {
    case 'urgent':
      return 'destructive';
    case 'high':
      return 'warning';
    case 'normal':
      return 'default';
    case 'low':
    default:
      return 'secondary';
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatSalary(min: number | null, max: number | null, currency: string): string {
  if (!min && !max) return 'Not specified';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  });
  if (min && max) {
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  }
  if (min) {
    return `From ${formatter.format(min)}`;
  }
  return `Up to ${formatter.format(max!)}`;
}

function JobDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-8 w-8 rounded" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton className="h-64 lg:col-span-2" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {
    data: job,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['job', id],
    queryFn: () => fetchJob(id!),
    enabled: !!id,
  });

  const { data: applicationsData } = useQuery({
    queryKey: ['job-applications', id],
    queryFn: () => fetchJobApplications(id!, { limit: 10 }),
    enabled: !!id,
  });

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: () => deleteJob(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      navigate('/jobs');
    },
    onError: (err) => {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete job');
    },
  });

  if (isLoading) {
    return <JobDetailSkeleton />;
  }

  if (error || !job) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Job Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The job you are looking for does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/jobs">Back to Jobs</Link>
          </Button>
        </div>
      </div>
    );
  }

  const applications = applicationsData?.data ?? [];
  const applicationCount = applicationsData?.pagination.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div>
        <Link
          to="/jobs"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Jobs
        </Link>
      </div>

      {/* Job Header */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-6">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                <Briefcase className="h-7 w-7 text-emerald-500" />
                {job.title}
              </h1>
              <div className="flex gap-2">
                <Badge variant={getStatusBadgeVariant(job.status)}>
                  {STATUS_LABELS[job.status]}
                </Badge>
                <Badge variant={getPriorityBadgeVariant(job.priority)}>
                  {capitalizeFirst(job.priority)}
                </Badge>
              </div>
            </div>

            {job.company && (
              <p className="text-lg text-text-secondary mt-2 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-text-tertiary" />
                {job.company.name}
              </p>
            )}

            <div className="flex flex-wrap gap-4 mt-4 text-sm text-text-tertiary">
              {job.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {job.location}
                  {job.remoteType && ` (${job.remoteType})`}
                </div>
              )}
              {job.jobType && (
                <div className="flex items-center gap-1">
                  <Briefcase className="h-4 w-4" />
                  {JOB_TYPE_LABELS[job.jobType as JobType] || job.jobType}
                </div>
              )}
              <div className="flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                {formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency)}
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {applicationCount} Application{applicationCount !== 1 ? 's' : ''}
              </div>
              {job.deadline && (
                <div className={`flex items-center gap-1 ${new Date(job.deadline) < new Date() ? 'text-red-600' : ''}`}>
                  <Calendar className="h-4 w-4" />
                  Deadline: {formatDate(job.deadline)}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to={`/jobs/${id}/edit`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Description & Requirements */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Description</CardTitle>
            </CardHeader>
            <CardContent>
              {job.description ? (
                <div
                  className="prose prose-sm prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: job.description }}
                />
              ) : (
                <p className="text-text-tertiary">No description provided</p>
              )}
            </CardContent>
          </Card>

          {/* Requirements */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              {job.requirements ? (
                <div
                  className="prose prose-sm prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: job.requirements }}
                />
              ) : (
                <p className="text-text-tertiary">No requirements specified</p>
              )}
            </CardContent>
          </Card>

          {/* Required Skills */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Required Skills</CardTitle>
            </CardHeader>
            <CardContent>
              {job.requiredSkills && job.requiredSkills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {job.requiredSkills.map((skill) => (
                    <Badge key={skill} variant="secondary">
                      {skill}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-text-tertiary">No skills specified</p>
              )}
            </CardContent>
          </Card>

          {/* Applications List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Applications ({applicationCount})</CardTitle>
              <Button variant="outline" size="sm" asChild>
                <Link to={`/pipeline?jobId=${id}`}>View in Pipeline</Link>
              </Button>
            </CardHeader>
            <CardContent>
              {applications.length > 0 ? (
                <div className="space-y-4">
                  {applications.map((application) => (
                    <div
                      key={application.id}
                      className="flex items-center justify-between p-3 bg-bg-secondary rounded-lg"
                    >
                      <Link
                        to={`/candidates/${application.candidateId}`}
                        className="flex items-center gap-3 hover:text-emerald-500"
                      >
                        <Avatar className="h-9 w-9">
                          <AvatarFallback>
                            {application.candidate
                              ? getInitials(application.candidate.firstName, application.candidate.lastName)
                              : '??'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">
                            {application.candidate
                              ? `${application.candidate.firstName} ${application.candidate.lastName}`
                              : 'Unknown Candidate'}
                          </div>
                          {application.candidate?.currentJobTitle && (
                            <div className="text-sm text-text-tertiary">
                              {application.candidate.currentJobTitle}
                            </div>
                          )}
                        </div>
                      </Link>
                      <Badge variant="secondary">
                        {STAGE_LABELS[application.currentStage]}
                      </Badge>
                    </div>
                  ))}
                  {applicationCount > applications.length && (
                    <div className="text-center pt-2">
                      <Link
                        to={`/pipeline?jobId=${id}`}
                        className="text-sm text-emerald-500 hover:underline"
                      >
                        View all {applicationCount} applications
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-text-tertiary">
                  <Users className="h-8 w-8 mb-2" />
                  <p>No applications yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Job Details */}
        <div className="space-y-6">
          {/* Job Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Job Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">Positions Available</dt>
                  <dd className="text-text-primary mt-1">{job.positionsAvailable}</dd>
                </div>
                {(job.experienceMin !== null || job.experienceMax !== null) && (
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Experience Required</dt>
                    <dd className="text-text-primary mt-1 flex items-center gap-1">
                      <Clock className="h-4 w-4 text-text-tertiary" />
                      {job.experienceMin !== null && job.experienceMax !== null
                        ? `${job.experienceMin} - ${job.experienceMax} years`
                        : job.experienceMin !== null
                          ? `${job.experienceMin}+ years`
                          : `Up to ${job.experienceMax} years`}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-text-tertiary">Remote Type</dt>
                  <dd className="text-text-primary mt-1">
                    {job.remoteType || 'Not specified'}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Timeline Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-tertiary" />
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Created</dt>
                    <dd className="text-text-primary">{formatDate(job.createdAt)}</dd>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-tertiary" />
                  <div>
                    <dt className="text-sm font-medium text-text-tertiary">Last Updated</dt>
                    <dd className="text-text-primary">{formatDate(job.updatedAt)}</dd>
                  </div>
                </div>
                {job.deadline && (
                  <div className="flex items-center gap-2">
                    <Calendar className={`h-4 w-4 ${new Date(job.deadline) < new Date() ? 'text-red-500' : 'text-text-tertiary'}`} />
                    <div>
                      <dt className="text-sm font-medium text-text-tertiary">Deadline</dt>
                      <dd className={new Date(job.deadline) < new Date() ? 'text-red-600' : 'text-text-primary'}>
                        {formatDate(job.deadline)}
                        {new Date(job.deadline) < new Date() && ' (Expired)'}
                      </dd>
                    </div>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Created By Card */}
          {job.createdBy && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Created By</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>
                      {getInitials(job.createdBy.firstName, job.createdBy.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">
                      {job.createdBy.firstName} {job.createdBy.lastName}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <StickyNote className="h-5 w-5" />
            Notes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <NotesSection entityType="job" entityId={id!} />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Job
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{job.title}"? This action cannot be undone.
              {applicationCount > 0 && (
                <span className="block mt-2 text-red-600">
                  Warning: This job has {applicationCount} application{applicationCount !== 1 ? 's' : ''} that will also be affected.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{deleteError}</span>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
